/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import ruTranslations from '../locales/ru.json';
import uzTranslations from '../locales/uz.json';

type Language = 'RU' | 'UZ';

interface LanguageContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const translations: Record<Language, Record<string, string>> = {
  RU: ruTranslations,
  UZ: uzTranslations,
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [lang, setLangState] = useState<Language>(() => {
    if (user?.lang) return user.lang as Language;
    if (typeof window !== 'undefined') {
      try {
        const storedUser = localStorage.getItem('erp_user');
        if (storedUser) {
          const u = JSON.parse(storedUser);
          if (u.lang) return u.lang as Language;
        }
      } catch {
        // ignore parse errors
      }
    }
    return 'RU';
  });

  const setLang = (newLang: Language) => {
    setLangState(newLang);
  };

  const t = useCallback(
    (key: string, params?: Record<string, string | number>) => {
      let text = translations[lang][key] || key;
      if (params) {
        Object.entries(params).forEach(([k, v]) => {
          text = text.replace(`{${k}}`, String(v));
        });
      }
      return text;
    },
    [lang],
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useTranslation = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useTranslation must be used within LanguageProvider');
  return context;
};
